import Navbar from "./Navbar";
import PriceChart from "./PriceChart";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FADE_DOWN_ANIMATION_VARIANTS } from "../lib/framer";
import Splinen from "./Splinen";
import animationvideo from "../assets/animationvideo.png";
import whitepaperPdf from '../assets/whitepaper.pdf'
import { useState, useEffect } from "react";

function Hero() {
  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const [priceData, setPriceData] = useState({
    price: null,
    change24h: null,
    high24h: null,
    low24h: null,
    volume24h: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        let response;
        let apiUrl;

        // Check if we're in production (deployed) or local development
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          // In local development, try CORS proxy
          const corsProxies = [
            'https://corsproxy.io/?',
            'https://api.codetabs.com/v1/proxy?quest=',
          ];

          for (const proxy of corsProxies) {
            try {
              apiUrl = proxy + encodeURIComponent('https://api-cloud.bitmart.com/spot/quotation/v3/ticker?symbol=DGFL_USDT');
              response = await fetch(apiUrl);
              if (response.ok) break;
            } catch (err) {
              continue;
            }
          }
        } else {
          // In production (cPanel), use PHP proxy
          apiUrl = '/api/bitmart-price.php';
          response = await fetch(apiUrl);
        }

        if (!response || !response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();

        if (data.code === 1000 && data.data) {
          const ticker = data.data;
          const currentPrice = parseFloat(ticker.last);
          const priceChange = parseFloat(ticker.fluctuation) * 100;

          setPriceData({
            price: currentPrice.toFixed(6),
            change24h: priceChange.toFixed(2),
            high24h: parseFloat(ticker.high_24h).toFixed(6),
            low24h: parseFloat(ticker.low_24h).toFixed(6),
            volume24h: parseFloat(ticker.v_24h).toLocaleString('en-US', { maximumFractionDigits: 0 }),
            loading: false,
            error: null
          });
        } else {
          throw new Error('Invalid data format');
        }
      } catch (error) {
        console.error('Price fetch error:', error);
        setPriceData(prev => ({
          ...prev,
          loading: false,
          error: 'Unable to load price data'
        }));
      }
    };

    fetchPrice();
    const interval = setInterval(fetchPrice, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>
    <div
      id="/"
      className="overflow-hidden bg-gradient-to-tr from-gray-500 via-green-100/50 to-gray-400"
    >
      {/* NAVBAR */}
      <Navbar />

      {/* Content */}
      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? "show" : "hidden"}
        viewport={{ once: true }}
        variants={{
          hidden: {},
          show: {
            transition: {
              staggerChildren: 0.15,
            },
          },
        }}
        className="flex flex-col xl:flex-row  gap-10   p-3   md:pb-[4rem] md:px-[6rem] dark:bg-black"
      >
        {/* left */}
        <div className="xl:w-[90rem] flex flex-col xl:pt-[2.5rem] lg:pt-10 justify-center items-center xl:justify-start xl:items-start  py-1">
          {/* text */}
          <div className="">
            <motion.h1
              variants={FADE_DOWN_ANIMATION_VARIANTS}
              className="text2 font-bold text-[2.5rem] md:text-[3.5rem] text-heading dark:text-gray-300 p-4 pb-0 md:p-0 text-center xl:text-start"
            >
              Dive into the Future of Investing with{" "}
              <span className="text-brand">DigiFolio</span>
            </motion.h1>
            <motion.p
              variants={FADE_DOWN_ANIMATION_VARIANTS}
              className="text1 font-normal text-[1rem] mt-6 text-para dark:text-gray-300 p-4 md:p-0 text-center xl:text-start"
            >
              Your gateway to cutting-edge cryptocurrency opportunities. Explore
              innovative investment strategies tailored to your goals and
              experience seamless, user-friendly trading. Join us today and
              embark on a journey to financial success with DigiFolio.
            </motion.p>
          </div>
          {/* Buttons */}
          <motion.div
            variants={FADE_DOWN_ANIMATION_VARIANTS}
            className="flex gap-6 mt-10 "
          >
            <a href={"https://www.bitmart.com/en-GB/trade/DGFL_USDT?type=spot"} target="_blank"
              type="button"
              className="text-white  text2 bg-brand hover:bg-[#00815F]  border-1 border-brand  font-medium rounded-full text-[1.1rem] px-7 py-2.5 text-center inline-flex items-center dark:bg-brand dark:hover:bg-[#00815F] transition duration-150 ease-in-out"
            >
              Buy $DGFL
              <svg
                className="rtl:rotate-180 w-3.5 h-3.5 ms-2"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 10"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M1 5h12m0 0L9 1m4 4L9 9"
                />
              </svg>
            </a>
            <button
              type="button"
              onClick={() => {
                const link = document.createElement('a');
                link.href = whitepaperPdf; // Path to your PDF file
                link.setAttribute('download', 'whitepaper.pdf'); // Name of the downloaded file
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              className="text-brand text2  hover:bg-brand  font-medium rounded-full text-[1.1rem] px-5 py-2.5 text-center inline-flex items-center    border-1 border-brand hover:text-white hover:border-brand transition duration-150 ease-in-out light:bg-gradient-to-tl from-gray-300/80 via-gray-200/80 to-gray-300/80  dark:bg-transparent dark:text-gray-400 dark:border-gray-600 dark:hover:text-brand dark:hover:border-brand dark:hover:bg-transparent   "
            >
              Whitepaper
            </button>
          </motion.div>

          {/* Boxes Container - Live Price and Token Info */}
          <div className="flex flex-col gap-4 mt-[4.5rem] w-full">
            {/* Live Price Section */}
            <motion.div
              variants={FADE_DOWN_ANIMATION_VARIANTS}
              className="w-full"
            >
              <div className="border-2 dark:border-gray-700 rounded-[1.5rem] p-5 bg-gradient-to-br from-slate-50 via-gray-300 to-stone-50 dark:from-gray-800 dark:to-gray-800 boxShadow">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Left Section - Title and Price */}
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                      <h3 className="text-lg md:text-xl font-bold text-heading dark:text-gray-200">Live Price</h3>
                      <span className="text-xs md:text-sm text-para dark:text-gray-400">DGFL/USDT</span>
                    </div>

                    {!priceData.loading && !priceData.error && (
                      <div className="flex items-baseline gap-3">
                        <span className="text-2xl md:text-3xl font-bold text-heading dark:text-gray-200">
                          ${priceData.price}
                        </span>
                        <span className={`text-base md:text-lg font-semibold px-2.5 py-1 rounded-full ${
                          parseFloat(priceData.change24h) >= 0
                            ? 'text-green-600 bg-green-100 dark:bg-green-900/30'
                            : 'text-red-600 bg-red-100 dark:bg-red-900/30'
                        }`}>
                          {parseFloat(priceData.change24h) >= 0 ? '+' : ''}{priceData.change24h}%
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Right Section - Stats */}
                  {priceData.loading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-6 h-6 border-4 border-brand border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : priceData.error ? (
                    <div className="text-red-500 text-sm">{priceData.error}</div>
                  ) : (
                    <div className="flex gap-4 sm:gap-6">
                      <div className="flex flex-col">
                        <p className="text-xs text-para dark:text-gray-400">24h High</p>
                        <p className="text-sm md:text-base font-semibold text-heading dark:text-gray-200">${priceData.high24h}</p>
                      </div>
                      <div className="flex flex-col">
                        <p className="text-xs text-para dark:text-gray-400">24h Low</p>
                        <p className="text-sm md:text-base font-semibold text-heading dark:text-gray-200">${priceData.low24h}</p>
                      </div>
                      <div className="flex flex-col">
                        <p className="text-xs text-para dark:text-gray-400">24h Volume</p>
                        <p className="text-sm md:text-base font-semibold text-heading dark:text-gray-200">{priceData.volume24h}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Token Info box */}
            <motion.div
              variants={FADE_DOWN_ANIMATION_VARIANTS}
              className="w-full"
            >
              <div className="border-2 dark:border-gray-700 rounded-[1.5rem] p-5 bg-gradient-to-tr from-slate-50 via-gray-300 to-stone-50 dark:bg-gray-800 dark:from-gray-800 dark:to-gray-800 boxShadow">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Title */}
                  <h3 className="text-lg md:text-xl font-bold text-heading dark:text-gray-200">
                    Token Information
                  </h3>

                  {/* Token Details */}
                  <div className="flex flex-wrap gap-4 sm:gap-8">
                    <div className="flex flex-col">
                      <p className="text-xs text-para dark:text-gray-400">Token Name</p>
                      <p className="text-sm md:text-base font-semibold text-heading dark:text-gray-200">DIGI Token</p>
                    </div>

                    <div className="flex flex-col">
                      <p className="text-xs text-para dark:text-gray-400">Symbol</p>
                      <p className="text-sm md:text-base font-semibold text-heading dark:text-gray-200">DGFL</p>
                    </div>

                    <div className="flex flex-col">
                      <p className="text-xs text-para dark:text-gray-400">Decimal</p>
                      <p className="text-sm md:text-base font-semibold text-heading dark:text-gray-200">8</p>
                    </div>

                    <div className="flex flex-col">
                      <p className="text-xs text-para dark:text-gray-400">Chain</p>
                      <a href="https://bscscan.com/address/0x0542b4c905f439a783b4b513b82a7e8c1537ffe1" target="_blank" rel="noopener noreferrer">
                        <p className="text-sm md:text-base font-semibold text-heading dark:text-gray-200 hover:text-brand transition-colors">BEP20</p>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* right/ */}
        <motion.div
          variants={FADE_DOWN_ANIMATION_VARIANTS}
          className="animate-[wiggle_20s_ease-in-out_infinite] "
        >
          <img
            src={animationvideo}
            className="block md:hidden tab:hidden  mt-[0.1rem] mb-[2rem] max-w-[100%]"
            alt=""
          />
        </motion.div>
        <Splinen />

        {/* PRICE CHART */}
        </motion.div>
        <PriceChart />
        </div>

   
      
   </>
  );
}

export default Hero;
